import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FileText } from "lucide-react";

// TermsUpdateModal: a blocking modal shown to any logged-in user whose stored
// `terms_version` is older than the platform's `current_terms_version`. They
// must re-check the consent box and submit to continue using the app.
//
// Mounted globally in App.js so it appears regardless of route. It intentionally
// has NO close-on-backdrop and NO Escape close — the user must explicitly
// accept (or log out via the navbar) to dismiss it.
const TermsUpdateModal = () => {
  const { user, refreshUser } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset local state whenever the user changes (e.g., logout → login).
  useEffect(() => {
    setAccepted(false);
    setError("");
  }, [user?.id, user?.current_terms_version]);

  if (!user) return null;
  // Users who have NEVER accepted go through the signup checkbox flow,
  // not this re-acceptance modal.
  if (!user.terms_accepted) return null;
  // Already on the current version → nothing to do.
  if (user.terms_version === user.current_terms_version) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) {
      setError("Please agree to the updated Terms of Service and Privacy Policy to continue.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await api.post("/auth/accept-terms", { terms_version: user.current_terms_version });
      toast.success("Thanks — you're all set.");
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not record your acceptance.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-mm-navy/70 backdrop-blur-sm flex items-center justify-center px-4"
      data-testid="terms-update-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-update-title"
    >
      <form
        onSubmit={submit}
        className="bg-white rounded-2xl max-w-md w-full p-7 shadow-2xl border-2 border-mm-gold/40"
      >
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-mm-gold/20 text-mm-navy flex items-center justify-center shrink-0">
            <FileText size={20} strokeWidth={2.5} />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">Trail update</div>
            <h2 id="terms-update-title" className="font-heading text-2xl font-black text-mm-navy mt-1">
              We've updated our Terms
            </h2>
          </div>
        </div>

        <p className="text-sm text-mm-navy/80 mt-4 leading-relaxed">
          Since you last accepted, we've published a new version of our legal documents.
          Please take a moment to review them — we'll only ask once per update.
        </p>

        <div className="mt-4 rounded-lg bg-mm-parchment border border-mm-navy/10 p-3 text-xs text-mm-navy/70 flex flex-wrap gap-3" data-testid="terms-update-version-info">
          <span>
            Previously accepted: <span className="font-mono text-mm-navy">{user.terms_version || "—"}</span>
          </span>
          <span aria-hidden="true">·</span>
          <span>
            Now current: <span className="font-mono text-mm-navy">{user.current_terms_version}</span>
          </span>
        </div>

        <label className="mt-5 flex items-start gap-3 cursor-pointer select-none" htmlFor="terms-update-checkbox">
          <input
            id="terms-update-checkbox"
            type="checkbox"
            checked={accepted}
            onChange={(e) => {
              setAccepted(e.target.checked);
              if (e.target.checked) setError("");
            }}
            className="mt-1 h-4 w-4 accent-mm-gold shrink-0"
            data-testid="terms-update-checkbox"
            aria-describedby={error ? "terms-update-error" : undefined}
            aria-invalid={!!error}
          />
          <span className="text-sm text-mm-navy/90 leading-snug">
            I have read and agree to the updated{" "}
            <Link
              to="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold text-mm-navy underline hover:text-mm-steel"
              data-testid="terms-update-terms-link"
            >
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link
              to="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold text-mm-navy underline hover:text-mm-steel"
              data-testid="terms-update-privacy-link"
            >
              Privacy Policy
            </Link>.
          </span>
        </label>

        {error && (
          <p id="terms-update-error" role="alert" className="mt-3 text-sm text-red-700 font-bold" data-testid="terms-update-error">
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end">
          <button
            type="submit"
            disabled={submitting}
            className="btn-trail btn-gold text-sm"
            data-testid="terms-update-submit-btn"
          >
            {submitting ? "Saving..." : "Continue"}
          </button>
        </div>

        <p className="text-[11px] text-mm-navy/55 mt-4">
          Need a break? Use the account menu to log out — we'll prompt again next time you log in.
        </p>
      </form>
    </div>
  );
};

export default TermsUpdateModal;
